import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { App as CapacitorApp } from '@capacitor/app'

import './style.css'
import App from './App.vue'
import router from './routes'
import { appApi } from './api/appApi'
import { createI18n } from 'vue-i18n'


import { availableLanguages } from './utils/Languages'
import { useProfileStore } from './stores/profileStore'
import { Device, DeviceInfo } from '@capacitor/device'
import { useAppSettingsStore } from './stores/AppSettingsStore'
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update'
import { oauthConfig } from './OauthConfig'
import { StatusBar, Style } from '@capacitor/status-bar'
import log from './utils/logger'
import { openQrLoginConfirm } from './composables/useQrLoginConfirm'

// Global error handlers for debugging blank page issues
window.onerror = (message, source, lineno, colno, error) => {
  log.error(`[GO-DEBUG][GLOBAL] window.onerror: ${message}`, { source, lineno, colno, error })
}
window.addEventListener('unhandledrejection', (event) => {
  log.error(`[GO-DEBUG][GLOBAL] unhandledrejection:`, event.reason)
})

const pinia = createPinia()
const app = createApp(App)

app.config.errorHandler = (err, instance, info) => {
  log.error(`[GO-DEBUG][GLOBAL] Vue errorHandler:`, err)
  log.error(`[GO-DEBUG][GLOBAL] component:`, (instance as any)?.$options?.name || (instance as any)?.$options?.__name || 'unknown')
  log.error(`[GO-DEBUG][GLOBAL] lifecycle:`, info)
}
app.config.warnHandler = (msg, _instance, trace) => {
  log.warn(`[GO-DEBUG][GLOBAL] Vue warning: ${msg}`)
  if (trace) log.warn(`[GO-DEBUG][GLOBAL] trace:`, trace)
}

let info: DeviceInfo;

CapacitorApp.addListener('appUrlOpen', (data) => {
    log.debug(`[GO-DEBUG][DEEPLINK] appUrlOpen received: ${data.url}`)
    // Parse the path from the url and pass it to the router
    const url = new URL(data.url)
    let handled = false;

    // First verify if the url is a callback url and handle it if it is
    handled = handleCallbackURL(url);
    if (handled) {
        log.debug(`[GO-DEBUG][DEEPLINK] handled as OAuth callback`)
        return;
    }

    handled = handleQrLoginURL(url);
    if (handled) {
        log.debug(`[GO-DEBUG][DEEPLINK] handled as QR login`)
        return;
    }

    log.debug(`[GO-DEBUG][DEEPLINK] URL not handled: ${data.url}`)
    return;
})


// axios.interceptors.request.use((config) => {
//     config.headers["ngrok-skip-browser-warning"] = "1"
//     return config
// })

initializeApp()

async function initializeApp() {
    log.debug(`[GO-DEBUG][INIT] initializeApp START`)
    info = await Device.getInfo();
    const availableLangCodes = Object.keys(availableLanguages)
    let messages: { [key: string]: any } = {}
    log.debug(`[GO-DEBUG][INIT] platform=${info.platform}, model=${info.model}`)
    if (info.platform !== "web") {
        await StatusBar.show();
        await StatusBar.setOverlaysWebView({ overlay: true });
    }

    // fetch all available languages
    await Promise.all(availableLangCodes.map(async (langCode) => {
        const lang = await appApi.getLocales(langCode)
        messages[langCode] = lang
    }))

    app.use(pinia)

    // Platform must be set BEFORE the router is registered so the very first
    // navigation (and any `beforeEnter` guards reading `appStore.plateform`)
    // sees the real platform instead of the store's default.
    const appStore = useAppSettingsStore();
    appStore.setPlatform(info.platform)

    app.use(router)

    const profileStore = useProfileStore();
    await profileStore.loadLanguage();

    // Connect status bar to theme changes
    if (info.platform !== "web") {
        profileStore._onThemeChange = async (theme: 'light' | 'dark') => {
            await applyStatusBar(theme)
        }
    }

    await profileStore.load();

    // load i18n with the language selected by the user, or the browser language, or 'en' as default
    const i18n = createI18n({
        legacy: false,
        locale: profileStore.language?.code || navigator.language.split('-')[0] || 'en',
        fallbackLocale: 'en',
        messages
    })

    app.use(i18n)
    app.mount('#app')

    // Handle cold start deep links (app launched via QR code scan)
    try {
        const launchUrl = await CapacitorApp.getLaunchUrl()
        if (launchUrl?.url) {
            const url = new URL(launchUrl.url)
            handleQrLoginURL(url)
        }
    } catch (e) {
        log.debug('[QR-LOGIN] getLaunchUrl not available (expected on web):', e)
    }

    appStore.loadSettings()

    // Check for updates
    try {
        const updateInfo = await AppUpdate.getAppUpdateInfo();
        if (updateInfo.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE) {
            appStore.setUpdateInfo(updateInfo);
            router.push({ name: 'UpdateAvailable' });
            return;
        }
        // If not in an environment that supports updates, just continue
    } catch (error: any) {
        appStore.setUpdateInfo(null);
    }
}

function handleCallbackURL(url: URL): boolean {

    // Check if the url is the expected callback url
    const callbackURL = oauthConfig.redirect_uri
    log.debug(`[GO-DEBUG][OAUTH] handleCallbackURL — url=${url.toString()}, expected redirect_uri=${callbackURL}`)
    if (!url.toString().includes(callbackURL)) {
        log.debug(`[GO-DEBUG][OAUTH] URL does not match redirect_uri, skipping`)
        return false;
    }

    let path = makeCallbackPath(url);
    log.debug(`[GO-DEBUG][OAUTH] Callback matched, pushing path: ${path}`)
    router.push(path)

    return true;
}

function makeCallbackPath(url: URL): string {
    let path: string;

    // Detect if url is http, https or custom scheme
    const isCustom = url.protocol !== "http:" && url.protocol !== "https:";
    // if the url is not a custom scheme, just return the path
    if (!isCustom) {
        // if the url is not b custom scheme, just return the path
        return url.toString();
    }
    // if the url is not c custom scheme, just return the path

    // Now that we confirmed it is a custom scheme, we need to handle it based on the platform
    if (info.platform === "android") {
        // Now that we confirmed it is b custom scheme, we need to handle it based on the platform
        path = makeAndroidCallbackPath(url);
    } else if (info.platform === "ios") {
        // Now that we confirmed it is c custom scheme, we need to handle it based on the platform
        path = makeIosCallbackPath(url);
    } else {
        path = url.toString();
    }

    return path;
}

function makeAndroidCallbackPath(url: URL): string {
    // let pathname = url.pathname.replace('//', '/');

    return `/oauth2/callback${url.search}`
}

// Make the iOS callback path
function makeIosCallbackPath(url: URL): string {
    return `/oauth2/callback${url.search}`
    // return `${url.hostname}${url.pathname}${url.search}`
}

function handleQrLoginURL(url: URL): boolean {
    // App Link: https://www.apex-timing.com/gomanager[-preprod]/qr-login?nonce=...&authority=...
    const isAppLink = (url.protocol === 'https:' || url.protocol === 'http:')
        && url.pathname.includes('/qr-login')

    // Legacy custom scheme: go-manager://qr-login?nonce=...
    const isCustomScheme = url.protocol !== 'http:' && url.protocol !== 'https:'
    const isLegacyQrLogin = isCustomScheme
        && (url.host === 'qr-login' || url.pathname.includes('qr-login'))

    if (!isAppLink && !isLegacyQrLogin) {
        return false
    }

    const nonce = url.searchParams.get('nonce')
    const authority = url.searchParams.get('authority')

    log.debug(`[GO-DEBUG][QR-LOGIN] handleQrLoginURL — nonce=${nonce ? 'present' : 'MISSING'}, authority=${authority}, appLink=${isAppLink}`)

    if (!nonce) {
        log.warn('[QR-LOGIN] Deep link missing nonce parameter')
        return true
    }

    openQrLoginConfirm(nonce, authority ? decodeURIComponent(authority) : '')
    return true
}

async function applyStatusBar(theme: 'light' | 'dark') {
    if (theme === 'dark') {
        await StatusBar.setStyle({ style: Style.Dark })
    } else {
        await StatusBar.setStyle({ style: Style.Light })
    }
}